/* eslint-disable sort-keys */
import { createTheme } from "@mui/material";
import "@mui/x-data-grid/themeAugmentation"; // Import the theme augmentation

declare module "@mui/material/styles" {
  interface Palette {
    light: Palette["primary"];
  }
  interface PaletteOptions {
    light?: PaletteOptions["primary"];
  }
}

declare module "@mui/material/Tabs" {
  interface TabsPropsVariantOverrides {
    soft: true; // Allow 'soft' as a valid variant
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    light: true;
  }
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#F57F43",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#592627",
      contrastText: "#FFFFFF",
    },
    light: {
      main: "#f5f4f2", // Light gray background
      contrastText: "#333333", // Dark text color
    },
    background: {
      // Default background color
      default: "#f4f6f8",
    }
  },
  typography: {
    fontFamily: "Inter, sans-serif",
    h1: {
      fontSize: "16px",
      fontWeight: 600,
    },
    h5: {
      fontSize: "14px",
      fontWeight: 600,
    },
    h6: {
      fontSize: "12px",
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          textTransform: "none", // Disable uppercase transformation
          borderRadius: "5px", // Rounded corners
          boxShadow: "none",

          "&:hover": {
            boxShadow: "none",
          },

          "&.MuiButton-containedLight": {
            "&:hover": {
              background: "#f0eeeb",
            },
          },

          // Customizing border color for outlined secondary buttons
          ...(ownerState.variant === "outlined" &&
            ownerState.color === "secondary" && {
            borderColor: "rgba(0, 0, 0, 0.23)",
          }),
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          // Custom padding for all MuiCardContent components
          padding: "15px",
          "&:last-child": {
            // Custom padding for the last child
            paddingBottom: "15px",
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          // Rounded corners for input
          borderRadius: "8px",
        },
        inputMultiline: {
          // Specific styles for multiline input
          padding: "0px 0px", // Adjust padding for multiline
          resize: "block",
        },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: "14px",
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: "14px",
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: "#F3F1F1",
          "& .MuiTableRow-root": {
            fontSize: "14px",
            border: "1px solid #D5D5D5",
            "& .MuiTableCell-root": {
              border: "none",
              height: "56px"
            },

          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:last-child td, &:last-child th": { border: 0 },
          "& td, & th": { padding: "12px 16px" },

        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root": {
            "& .MuiTableCell-root": {
              borderTop: "1px solid #D5D5D5",
              borderBottom: "1px solid #D5D5D5",
            },
            "& th:first-child, & td:first-child": {
              borderLeft: "1px solid #D5D5D5",

            },
            "& th:last-child, & td:last-child": {
              borderRight: "1px solid #D5D5D5",

            },
          },
          "& .MuiTableRow-root:nth-of-type(odd)": {
            backgroundColor: "#FCFAF6",
          },
          "& .MuiTableRow-root:nth-of-type(even)": {
            backgroundColor: "#FFFFFF",
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: "#000000", // Set the <thead> text color
          fontSize: "13px", // Set the <thead> font size
          fontWeight: 600, // Optional: make the text bold
          padding: "12px",
        },
        body: {
          color: "#333", // Set the <thead> text color
          fontSize: "13px", // Set the <thead> font size
          padding: "12px",
          height: "52px",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: "16px", // Set the desired font size
          backgroundColor: "#F57F43", // Set the desired background color
          color: "#fff", // Optional: change text color for better contrast
          padding: "10px 24px"
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "10px 24px",
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          "& .MuiDataGrid-row:nth-of-type(odd)": {
            backgroundColor: "#FCFAF6",
          },
          "& .MuiDataGrid-row:nth-of-type(even)": {
            backgroundColor: "#ffffff",
          },
          border: '1px solid #ccc',
          '& .MuiDataGrid-cell': {
            borderRight: '1px solid #ccc',
          },
          '& .MuiDataGrid-columnHeader': {
            borderRight: '1px solid #ccc',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: "13px",
          },
          '& .MuiDataGrid-columnHeader--pinnedLeft, & .MuiDataGrid-columnHeader--pinnedRight': {
            backgroundColor: 'inherit',
          },
          '& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight': {
            backgroundColor: 'inherit',
          },
          '& .MuiDataGrid-filler--pinnedLeft, & .MuiDataGrid-filler--pinnedRight': {
            backgroundColor: 'inherit', 
          },
          
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          opacity: "1",
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          "& p": {
            marginBottom: "initial",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          "& .MuiTypography-root": {
            fontSize: "13px",
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        icon: {
          fontSize: "24px", // Adjust the size as per your need
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.customVariant === "soft" && {
            backgroundColor: "#e3f2fd",
            borderRadius: "8px",
            padding: "5px",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: "bold",
              "&.Mui-selected": {
                backgroundColor: "#42a5f5",
                color: "white",
              },
            },
          }),
        }),
      },
    },
    MuiSnackbar: {
      styleOverrides: {
        root: {
          "& .MuiAlert-message": {
            fontSize: "15px",
          },
          "& .MuiPaper-root": {
            padding: "8px 18px",
          },
        },
      },
    },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: {
          fontSize: "13px",

          "& .MuiBreadcrumbs-ol": {
            "& .MuiBreadcrumbs-li": {

              "& .MuiButtonBase-root": {
                color: "#E0E0E0"
              },
              "& .MuiTypography-root": {
                color: "#E0E0E0",
                "&.active": {
                  color: "#fff"
                }
              }
            },
            "& .MuiBreadcrumbs-separator": {
              color: "#E0E0E0"
            }
          }

        }

      },
    },
  },
});

export default theme;
